import React, { useState, useEffect, useMemo } from 'react';

/**
 * StayRelaxed component for the CheckinComplete page
 * Displays a breathing exercise UI
 */
const StayRelaxed = () => {
    // Breathing techniques with different patterns
    const breathingTechniques = [
        {
            name: "4-4-4 Box Breathing",
            description: "Inhale for 4, hold for 4, exhale for 4",
            phases: [
                { name: 'Inhale', duration: 4, color: '#5f63f2' },
                { name: 'Hold', duration: 4, color: '#4caf50' },
                { name: 'Exhale', duration: 4, color: '#ff9800' },
            ],
            benefits: "Reduces stress and improves focus"
        },
        {
            name: "4-7-8 Relaxing Breath",
            description: "Inhale for 4, hold for 7, exhale for 8",
            phases: [
                { name: 'Inhale', duration: 4, color: '#5f63f2' },
                { name: 'Hold', duration: 7, color: '#4caf50' },
                { name: 'Exhale', duration: 8, color: '#ff9800' },
            ],
            benefits: "Helps with anxiety and sleep"
        },
        {
            name: "5-2-5 Energizing Breath",
            description: "Inhale for 5, hold for 2, exhale for 5",
            phases: [
                { name: 'Inhale', duration: 5, color: '#5f63f2' },
                { name: 'Hold', duration: 2, color: '#4caf50' },
                { name: 'Exhale', duration: 5, color: '#ff9800' },
            ],
            benefits: "Increases energy and alertness"
        },
        {
            name: "2-1-2 Quick Calm",
            description: "Inhale for 2, hold for 1, exhale for 2",
            phases: [
                { name: 'Inhale', duration: 2, color: '#5f63f2' },
                { name: 'Hold', duration: 1, color: '#4caf50' },
                { name: 'Exhale', duration: 2, color: '#ff9800' },
            ],
            benefits: "Quick stress relief"
        }
    ];
    
    // Randomly select a breathing technique when component mounts
    const [selectedTechniqueIndex, setSelectedTechniqueIndex] = useState(
        Math.floor(Math.random() * breathingTechniques.length)
    );
    
    // Get the current selected technique
    const currentTechnique = breathingTechniques[selectedTechniqueIndex];
    
    // Breathing phases: 0 = inhale, 1 = hold, 2 = exhale
    const [breathingPhase, setBreathingPhase] = useState(0);
    const [secondsLeft, setSecondsLeft] = useState(currentTechnique.phases[0].duration);
    const [isActive, setIsActive] = useState(false);
    
    // Colors for different phases - derived from current technique
    const phaseColors = useMemo(() => {
        return currentTechnique.phases.reduce((acc, phase, index) => {
            acc[index] = { bg: phase.color, text: phase.name };
            return acc;
        }, {});
    }, [currentTechnique]);
    
    // Animation styles based on breathing phase
    const getCircleStyle = () => {
        const baseStyle = {
            width: '150px',
            height: '150px',
            background: phaseColors[breathingPhase].bg,
            transition: 'all 1s ease-in-out'
        };
        
        // Add scale transform based on phase
        if (breathingPhase === 0) {
            // Inhale - grow
            return {
                ...baseStyle,
                transform: `scale(${1 + (1 - secondsLeft/4) * 0.2})` // Grow from 1x to 1.2x
            };
        } else if (breathingPhase === 1) {
            // Hold - stay expanded
            return {
                ...baseStyle,
                transform: 'scale(1.2)'
            };
        } else {
            // Exhale - shrink
            return {
                ...baseStyle,
                transform: `scale(${1.2 - (1 - secondsLeft/4) * 0.2})` // Shrink from 1.2x to 1x
            };
        }
    };
    
    // Timer effect for breathing cycle
    useEffect(() => {
        if (!isActive) return;
        
        const timer = setInterval(() => {
            if (secondsLeft > 1) {
                setSecondsLeft(secondsLeft - 1);
            } else {
                // Move to next phase
                const nextPhase = (breathingPhase + 1) % 3;
                setBreathingPhase(nextPhase);
                // Reset timer based on the duration of the next phase
                setSecondsLeft(currentTechnique.phases[nextPhase].duration);
            }
        }, 1000);
        
        return () => clearInterval(timer);
    }, [breathingPhase, secondsLeft, isActive, currentTechnique]);
    
    // Change to a different breathing technique
    const changeTechnique = () => {
        if (isActive) return; // Only allow changing when not active
        
        // Select a different technique randomly
        let newIndex;
        do {
            newIndex = Math.floor(Math.random() * breathingTechniques.length);
        } while (newIndex === selectedTechniqueIndex && breathingTechniques.length > 1);
        
        setSelectedTechniqueIndex(newIndex);
        setBreathingPhase(0);
        setSecondsLeft(breathingTechniques[newIndex].phases[0].duration);
    };
    
    // Toggle breathing animation
    const toggleBreathing = () => {
        // If starting the exercise, reset to initial state
        if (!isActive) {
            setBreathingPhase(0);
            setSecondsLeft(currentTechnique.phases[0].duration);
        }
        setIsActive(!isActive);
    };
    
    return (
        <div className="card h-100" style={{ backgroundColor: '#f8f9fe', borderRadius: '10px', border: 'none' }}>
            <div className="card-body p-4">
                <h5 className="mb-3">Stay Relaxed</h5>
                <p className="text-muted mb-2">Follow the breathing circle to stay calm</p>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{currentTechnique.name}</h6>
                    <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={changeTechnique} 
                        disabled={isActive}
                        style={{ fontSize: '0.8rem' }}
                    >
                        Try Different Technique
                    </button>
                </div>
                {/* <p className="text-muted small mb-3">{currentTechnique.description}</p> */}

                <div className="text-center my-4">
                    <div className="position-relative" style={{ width: '150px', height: '150px', margin: '0 auto' }}>
                        <div 
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={isActive ? getCircleStyle() : { width: '150px', height: '150px', background: '#5f63f2', transition: 'all 0.5s ease-in-out' }}
                        >
                            <div className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                                style={{ width: '80px', height: '80px' }}>
                                <i className="fas fa-lungs" style={{ fontSize: '2rem', color: phaseColors[breathingPhase].bg }}></i>
                            </div>
                        </div>
                        <div className="position-absolute" style={{ bottom: '-10px', left: '-10px', right: '0', textAlign: 'center' }}>
                            <span 
                                className="badge bg-white px-3 py-2" 
                                style={{ 
                                    fontSize: '1.2rem', borderRadius: '20px', 
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                    color: isActive ? phaseColors[breathingPhase].bg : '#5f63f2',
                                    cursor: 'pointer'
                                }}
                                onClick={toggleBreathing}
                            >
                                {isActive ? `${phaseColors[breathingPhase].text} (${secondsLeft})` : 'Start Breathing'}
                            </span>
                        </div>
                    </div>
                </div>

                <p className="text-center text-muted mt-3">{currentTechnique.description} seconds</p>
                <p className="text-center text-muted small">{isActive ? 'Click to pause' : 'Click Start to begin'}</p>
                <p className="text-center text-primary small mt-2"><i className="fas fa-info-circle mr-1"></i> {currentTechnique.benefits}</p>
            </div>
        </div>
    );
};

export default StayRelaxed;
